/**
 * WordPress-style storefront guard — renders children only when the plugin is active.
 */
import { Link } from 'react-router-dom'
import { useFeatures, useIsPluginActive, type FeatureFlags } from '@/hooks/useFeatures'

interface PluginGuardProps {
  pluginId: string
  /** Optional store setting (only applies when plugin is active). */
  requireFeature?: keyof Pick<FeatureFlags, 'enable_wishlist' | 'enable_reviews'>
  children: React.ReactNode
}

export function PluginGuard({ pluginId, requireFeature, children }: PluginGuardProps) {
  const { features, isLoading: featuresLoading } = useFeatures()
  const { isActive, isLoading: pluginLoading } = useIsPluginActive(pluginId)
  const isLoading = featuresLoading || pluginLoading

  if (isLoading) {
    return (
      <div className="flex min-h-[40vh] items-center justify-center">
        <div className="text-gray-400">Loading…</div>
      </div>
    )
  }

  const featureOff = requireFeature && !features[requireFeature]

  if (!isActive || featureOff) {
    return (
      <div className="flex min-h-[40vh] items-center justify-center">
        <div className="text-center">
          <p className="text-gray-500">This feature is currently not available.</p>
          <p className="mt-1 text-sm text-gray-400">
            {!isActive ? 'The required plugin is not active.' : 'This feature is disabled in store settings.'}
          </p>
          <Link to="/" className="mt-4 inline-block text-primary-600 hover:underline">
            ← Back to home
          </Link>
        </div>
      </div>
    )
  }

  return <>{children}</>
}

/** Guard using computed feature flag (plugin + store setting). */
export function FeatureGuard({
  enabled,
  children,
}: {
  enabled: boolean
  children: React.ReactNode
}) {
  const { isLoading } = useFeatures()

  if (isLoading) {
    return (
      <div className="flex min-h-[20vh] items-center justify-center">
        <div className="text-gray-400">Loading…</div>
      </div>
    )
  }

  if (!enabled) return null

  return <>{children}</>
}
